import React, { createContext, useContext, useMemo, useCallback, ReactNode } from 'react';
import { getFirestore, collection, CollectionReference } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { EnvironmentDocument, canEditEnvironment } from '@finvari/deliver';
import { useSubscribedList } from '../utils/firebaseHooks';
import { useConfig } from './ConfigContext';

// Serves the configured environments (the top-level `environments` collection) as their own
// domain context, peer to PullRequestsContext / DeploymentStatusContext / MasterCommitsContext.
interface EnvironmentsContextType {
  environments: EnvironmentDocument[];
  environmentsById: Record<string, EnvironmentDocument>;
  loading: boolean;
  error: string | null;
}

const EnvironmentsContext = createContext<EnvironmentsContextType | undefined>(undefined);

export const useConfiguredEnvironments = (): EnvironmentsContextType => {
  const context = useContext(EnvironmentsContext);
  if (context === undefined) {
    throw new Error('useConfiguredEnvironments must be used within an EnvironmentsProvider');
  }
  return context;
};

// Soft visibility rule for lightweight pickers: archived environments are hidden; a personal dev
// environment (ownerUid set) is hidden from everyone but its owner; and a pooled environment is
// hidden from everyone (you target the pool, not a member). Shared, so the raw-doc list and the
// computed-status list filter identically. The list's "Show all" view bypasses this entirely.
// (Deploy-target pickers additionally re-offer an environment the current user holds a lease on —
// that exception lives in those pages, not here, so the default-environment picker stays clean.)
export const isEnvironmentVisibleTo = (
  env: { ownerUid?: string; archived?: boolean; poolId?: string },
  uid: string | undefined
): boolean => !env.archived && (!env.ownerUid || env.ownerUid === uid) && !env.poolId;

// Raw environment docs visible to the current user (for lightweight deploy-target pickers).
// Filters the already-subscribed list in memory — no extra reads.
export const useVisibleEnvironments = (): EnvironmentDocument[] => {
  const { environments } = useConfiguredEnvironments();
  const { userProfile } = useConfig();
  const uid = userProfile?.uid;
  return useMemo(() => environments.filter((env) => isEnvironmentVisibleTo(env, uid)), [environments, uid]);
};

// Returns a predicate that answers "can the current user edit this environment?" — capturing the
// user profile and protected-environment list once so callers (incl. inside `.map()`) don't have
// to reassemble the three inputs `canEditEnvironment` needs.
export const useCanEditEnvironment = (): ((
  env: { gcpProjectId: string; ownerUid?: string }
) => boolean) => {
  const { config, userProfile } = useConfig();
  const protectedEnvironmentIds = config?.protectedEnvironments;
  return useCallback(
    (env) =>
      canEditEnvironment({ userProfile, environment: env, protectedEnvironmentIds: protectedEnvironmentIds ?? [] }),
    [userProfile, protectedEnvironmentIds]
  );
};

interface EnvironmentsProviderProps {
  children: ReactNode;
  user: User | null;
}

export const EnvironmentsProvider: React.FC<EnvironmentsProviderProps> = ({ children, user }) => {
  const environmentsQuery = useMemo(() => {
    if (!user) return undefined;
    const db = getFirestore();
    return collection(db, 'environments') as CollectionReference<EnvironmentDocument>;
  }, [user]);

  const { environments, loading, error } = useSubscribedList<EnvironmentDocument, 'environments'>(
    environmentsQuery,
    'environments'
  );

  const environmentsById = useMemo(() => {
    const byId: Record<string, EnvironmentDocument> = {};
    for (const env of environments) {
      byId[env.gcpProjectId] = env;
    }
    return byId;
  }, [environments]);

  const value: EnvironmentsContextType = { environments, environmentsById, loading, error };

  return (
    <EnvironmentsContext.Provider value={value}>
      {children}
    </EnvironmentsContext.Provider>
  );
};
